<script setup lang="ts">
import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';
import { SearchService as SharedSearchService } from '@/core/shared/search/search.service';
import { SelectedFilterType } from '@/core/sourcing/search/types/selected-filter-type.type';

// One mode checkbox per active filter, not per group: inference can give the two chips of one
// group different modes. The pay floor never filters, so pay passes only its ceiling.
const LABELS = {
  [SelectedFilterType.PAY_RATE_MAX]: 'Include candidates with no pay information',
  [SelectedFilterType.SHIFT]: 'Include candidates with no shift information',
  [SelectedFilterType.OPEN_TO_TRAVEL]: 'Include candidates with no travel information',
  [SelectedFilterType.WILLING_TO_RELOCATE]: 'Include candidates with no relocation information',
  [SelectedFilterType.LANGUAGE]: 'Include candidates with no language information',
  [SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES]:
    'Include candidates with no open-to-work information',
  [SelectedFilterType.RESPONSIVENESS]: 'Include candidates with no responsiveness information',
} as const;

type LabeledFilterType = keyof typeof LABELS;

const props = defineProps<{ types: readonly LabeledFilterType[] }>();

const sharedSearchService = new SharedSearchService();

function toggle(type: LabeledFilterType): void {
  sharedSearchService.setDossierIncludeUnknown(
    type,
    !sharedSearchService.getDossierIncludeUnknown(type),
  );
}
</script>

<template>
  <template v-for="type in props.types" :key="type">
    <div
      v-if="sharedSearchService.isDossierFilterActive(type)"
      class="include-unknown"
      :data-test="`smart-filters-include-unknown-${type}`"
    >
      <Checkbox
        class="mt-[2px]"
        :model-value="sharedSearchService.getDossierIncludeUnknown(type)"
        :aria-label="LABELS[type]"
        @update:model-value="sharedSearchService.setDossierIncludeUnknown(type, $event)"
      />
      <!-- The checkbox carries the accessible name; the text is a larger click target. -->
      <span aria-hidden="true" @click="toggle(type)">{{ LABELS[type] }}</span>
    </div>
  </template>
</template>

<style scoped lang="postcss">
.include-unknown {
  @apply mt-2 flex items-start gap-2 text-sm text-shade-800;

  span {
    @apply cursor-pointer text-balance;
  }
}
</style>
